import React, { useCallback, useEffect, useState } from 'react';

import type { DocNode } from '@atlaskit/adf-schema';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import type { AvailableSite, IssueCreateEmbedSuccessPayload } from '@atlassian/gic-anywhere';

import { SingleWorkCreateForm } from '../single-work-create-form/SingleWorkCreateForm';
import { useIssueCreateSidePanelActions } from '../../providers/IssueCreateSidePanelContextProvider';
import { SingleWorkCreateFormAiSupportedSection } from '../single-work-create-form/SingleWorkCreateFormAiSupportedSection';
import type { JiraProject } from '../../__types__/apiUtils';
import type { AiSectionProps } from '../../__types__/IssueCreateFormRenderer';

export interface IssueCreateFormRendererProps {
	onIssueCreateInit: () => void;
	onClose: () => void;
	onSuccess: (payload: IssueCreateEmbedSuccessPayload) => void;
	summary?: string;
	descriptionAdf?: DocNode;
	setOverrideCloudIdForSitePicker?: (cloudId: string) => void;
	setOverrideProjectId?: (projectId: string) => void;
	isCreatedWithAi: boolean;
	aiSection: (props: AiSectionProps) => React.ReactNode;
	setShouldSwitchToGicAnywhere: (shouldSwitch: boolean) => void;
}

export const IssueCreateFormRenderer: React.FC<IssueCreateFormRendererProps> = ({
	onIssueCreateInit,
	onSuccess,
	onClose,
	summary = '',
	descriptionAdf,
	setOverrideCloudIdForSitePicker,
	setOverrideProjectId,
	isCreatedWithAi = false,
	aiSection,
	setShouldSwitchToGicAnywhere,
}) => {
	const [currentSummary, setCurrentSummary] = useState<string>(summary);
	const [currentDescription, setCurrentDescription] = useState<DocNode | undefined>(descriptionAdf);
	const [isDescriptionRequired, setIsDescriptionRequired] = useState(false);
	const actions = useIssueCreateSidePanelActions();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		onIssueCreateInit?.();
	}, [onIssueCreateInit]);

	const handleShouldUseEmbed = useCallback(
		(shouldUse: { selectedSite: AvailableSite | null; selectedProject: JiraProject | null }) => {
			if (shouldUse) {
				setShouldSwitchToGicAnywhere(true);
				if (shouldUse.selectedSite) {
					void setOverrideCloudIdForSitePicker?.(shouldUse.selectedSite.cloudId);
				}
				if (shouldUse.selectedProject) {
					void setOverrideProjectId?.(shouldUse.selectedProject.id);
				}
				if (currentSummary) {
					void actions.setSummary(currentSummary);
				}
				if (currentDescription) {
					void actions.setDescriptionAdf(currentDescription);
				}
				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						source: 'confluenceHighlightIssueCreate',
						action: 'transitioned',
						actionSubject: 'singleWorkCreateForm',
						attributes: {
							isCreatedWithAi,
							isManuallyCreated: !isCreatedWithAi,
						},
					},
				}).fire();
			}
		},
		[
			currentSummary,
			currentDescription,
			actions,
			setOverrideCloudIdForSitePicker,
			setOverrideProjectId,
			setShouldSwitchToGicAnywhere,
			createAnalyticsEvent,
			isCreatedWithAi,
		],
	);

	const handleSummaryChange = useCallback((value: string) => {
		setCurrentSummary(value);
	}, []);

	const handleDescriptionChange = useCallback((value: DocNode | undefined) => {
		setCurrentDescription(value);
	}, []);

	return (
		<SingleWorkCreateForm
			onSuccess={onSuccess}
			closePanel={onClose}
			currentSummary={currentSummary}
			currentDescription={currentDescription}
			onShouldUseEmbed={handleShouldUseEmbed}
			onDescriptionRequiredChange={setIsDescriptionRequired}
			aiSupportedSection={
				!isCreatedWithAi ? (
					<SingleWorkCreateFormAiSupportedSection
						initialSummary={summary}
						initialDescription={descriptionAdf}
						handleSummaryChange={handleSummaryChange}
						handleDescriptionChange={handleDescriptionChange}
						isDescriptionRequired={isDescriptionRequired}
						isCreatedWithAi={false}
					/>
				) : (
					aiSection({
						handleSummaryChange,
						handleDescriptionChange,
						isDescriptionRequired,
					})
				)
			}
		/>
	);
};
